import {
  UnifiedAlertDefinition,
  UnifiedAlertRequest,
  UnifiedAlertEventsSearchRequest,
  UnifiedAlertEventsSearchResponse,
  UnifiedAlertsSearchRequest,
  UnifiedAlertsSearchResponse,
} from '@logz-pkg/models';
import { CrudApiService } from '../utilities/crud.api.service';

type AlertType = 'logs' | 'metrics';

class UnifiedAlertsApiService {
  private crudApiService = new CrudApiService();

  private urls = {
    searchEvents: '/v2/unified-alerts/events/search',
    search: '/v2/unified-alerts/search',
    resource: (type: AlertType, id?: string | number) => `/v2/unified-alerts/${type}${id ? `/${id}` : ''}`,
    enable: (type: AlertType, id: string | number) => `/v2/unified-alerts/${type}/${id}/enable`,
    disable: (type: AlertType, id: string | number) => `/v2/unified-alerts/${type}/${id}/disable`,
  };

  search = async (request: UnifiedAlertsSearchRequest): Promise<UnifiedAlertsSearchResponse> => {
    const response = await this.crudApiService.do<UnifiedAlertsSearchResponse>(this.urls.search, {
      payload: request,
    });

    return response ?? { results: [], total: 0, pagination: request.pagination };
  };

  searchEvents = async (request: UnifiedAlertEventsSearchRequest): Promise<UnifiedAlertEventsSearchResponse> => {
    const response = await this.crudApiService.do<UnifiedAlertEventsSearchResponse>(this.urls.searchEvents, {
      payload: request,
    });

    return response ?? { results: [], total: 0, pagination: request.pagination };
  };

  get = async (type: AlertType, id: string | number): Promise<UnifiedAlertDefinition> => {
    return this.crudApiService.get<UnifiedAlertDefinition>(this.urls.resource(type, id));
  };

  create = async (type: AlertType, request: UnifiedAlertRequest): Promise<UnifiedAlertDefinition> => {
    return this.crudApiService.do<UnifiedAlertDefinition>(this.urls.resource(type), { payload: request });
  };

  update = async (
    type: AlertType,
    id: string | number,
    request: UnifiedAlertRequest,
  ): Promise<UnifiedAlertDefinition> => {
    const { data } = await this.crudApiService.httpService.put(this.urls.resource(type, id), request);

    return data;
  };

  delete = async (type: AlertType, id: string | number): Promise<void> => {
    await this.crudApiService.delete(this.urls.resource(type, id));
  };

  enable = async (type: AlertType, id: string | number): Promise<void> => {
    await this.crudApiService.do(this.urls.enable(type, id));
  };

  disable = async (type: AlertType, id: string | number): Promise<void> => {
    await this.crudApiService.do(this.urls.disable(type, id));
  };
}

export const unifiedAlertsApiService = new UnifiedAlertsApiService();
